import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { getStatoGiorno } from '../../hooks/useCalendarioChiusure'
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  CheckCircle,
  Clock,
  AlertTriangle,
  Plus,
  List,
  X,
  Eye,
  Users
} from 'lucide-react'

/**
 * CalendarView - Vista calendario per registrazioni
 * 
 * Features:
 * - Colori sfondo giorno basati su completezza (verde/giallo/arancione)
 * - Bordo rosso per urgenze
 * - Tooltip hover (desktop) con lista minori
 * - Badge giorni incompleti nell'header
 * - Mini progress bar per giorno
 * - Integrazione chiusure calendario
 * - Indicatore festivo lavorato (sfondo viola)
 */
export default function CalendarView({ 
  entries = [], 
  minori = [],
  onDateSelect, 
  onMonthChange,
  selectedDate,
  loading = false,
  calendarioSettings = null,
  className = 'max-w-4xl mx-auto'
}) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [hoveredDate, setHoveredDate] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const calendarRef = useRef(null)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Genera giorni del mese
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    
    let startDay = firstDay.getDay()
    startDay = startDay === 0 ? 6 : startDay - 1
    
    const days = []
    
    const prevMonth = new Date(year, month, 0)
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      })
    }
    
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({
        date: new Date(year, month, d),
        isCurrentMonth: true
      })
    }
    
    const remainingDays = 42 - days.length
    for (let d = 1; d <= remainingDays; d++) {
      days.push({
        date: new Date(year, month + 1, d),
        isCurrentMonth: false
      })
    }
    
    return days
  }, [currentDate])

  // Mappa entries per data (fix timezone)
  const entriesByDate = useMemo(() => {
    const map = {}
    entries.forEach(entry => {
      const entryDate = new Date(entry.data)
      const dateKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`
      if (!map[dateKey]) map[dateKey] = []
      map[dateKey].push(entry)
    })
    return map
  }, [entries])

  // Calcola statistiche per un giorno
  const getDayStats = (date) => {
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    const dayEntries = entriesByDate[dateKey] || []
    
    // Ottieni stato giorno per chiusure
    const statoGiorno = calendarioSettings ? getStatoGiorno(date, calendarioSettings) : { aperto: true, tipologia: 'normale' }
    
    const minoriRegistratiIds = dayEntries.map(e => 
      (typeof e.minore === 'object' ? e.minore._id : e.minore)?.toString()
    )
    const minoriMancanti = minori.filter(m => !minoriRegistratiIds.includes(m._id?.toString()))
    
    const presenze = dayEntries.filter(e => 
      e.presente === true || e.presente === 'Sì' || e.presente === 'Presente'
    ).length
    const assenze = dayEntries.filter(e => 
      e.presente === false || e.presente === 'No' || e.presente === 'Assente'
    ).length
    const urgenze = dayEntries.filter(e => e.segnalazioneUrgente).length
    
    const totaleMinori = minori.length
    const percentuale = totaleMinori > 0 
      ? Math.round((dayEntries.length / totaleMinori) * 100) 
      : 0
    
    return {
      entries: dayEntries,
      minoriMancanti,
      minoriRegistrati: dayEntries.map(e => ({
        ...e,
        minoreNome: typeof e.minore === 'object' ? e.minore.nomeCompleto : 'Minore'
      })),
      presenze,
      assenze,
      urgenze,
      percentuale,
      totaleMinori,
      statoGiorno
    }
  }

  // Calcola giorni incompleti del mese corrente
  const giorniIncompleti = useMemo(() => {
    if (minori.length === 0) return 0
    
    let count = 0
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const lastDay = new Date(year, month + 1, 0).getDate()
    
    for (let d = 1; d <= lastDay; d++) {
      const date = new Date(year, month, d)
      if (date > today) continue
      
      const stats = getDayStats(date)
      // Non contare giorni chiusi come incompleti
      if (stats.statoGiorno?.aperto === false) continue
      
      if (stats.percentuale < 100 && stats.percentuale > 0) {
        count++
      }
    }
    
    return count
  }, [currentDate, entriesByDate, minori, today, calendarioSettings])

  // Colore sfondo giorno basato su completezza e stato
  const getDayBackgroundColor = (stats, isCurrentMonth) => {
    if (!isCurrentMonth) return 'bg-gray-50 dark:bg-gray-900/50'
    
    const tipologia = stats.statoGiorno?.tipologia
    
    // Festivo lavorato / Apertura straordinaria = sfondo viola
    if (['festivita_lavorata', 'patrono_lavorativo', 'apertura_straordinaria'].includes(tipologia)) {
      // Usa sfondo viola con completezza
      if (stats.totaleMinori > 0) {
        if (stats.percentuale === 100) {
          return 'bg-purple-100 dark:bg-purple-900/30'
        } else if (stats.percentuale >= 50) {
          return 'bg-purple-50 dark:bg-purple-900/20'
        } else if (stats.percentuale > 0) {
          return 'bg-purple-50/50 dark:bg-purple-900/10'
        }
      }
      return 'bg-purple-50 dark:bg-purple-900/20'
    }
    
    // Se giorno chiuso, sfondo grigio con pattern
    if (stats.statoGiorno && !stats.statoGiorno.aperto) {
      return 'bg-gray-100 dark:bg-gray-800 bg-stripes'
    }
    
    if (stats.totaleMinori === 0) return ''
    
    if (stats.percentuale === 100) {
      return 'bg-green-50 dark:bg-green-900/20'
    } else if (stats.percentuale >= 50) {
      return 'bg-yellow-50 dark:bg-yellow-900/20'
    } else if (stats.percentuale > 0) {
      return 'bg-orange-50 dark:bg-orange-900/20'
    }
    return ''
  }

  // Bordo rosso per urgenze
  const getDayBorderClass = (stats) => {
    if (stats.urgenze > 0) {
      return 'ring-2 ring-red-500 ring-inset'
    }
    return ''
  }

  // Navigazione mesi
  const goToPrevMonth = () => {
    const newDate = new Date(currentDate)
    newDate.setMonth(newDate.getMonth() - 1)
    setCurrentDate(newDate)
    onMonthChange?.(newDate)
  }

  const goToNextMonth = () => {
    const newDate = new Date(currentDate)
    newDate.setMonth(newDate.getMonth() + 1)
    setCurrentDate(newDate)
    onMonthChange?.(newDate)
  }

  const goToToday = () => {
    setCurrentDate(new Date())
    onMonthChange?.(new Date())
  }

  const handleDateClick = (date) => {
    onDateSelect?.(date)
  }

  // Hover handlers (solo desktop) - con fix posizionamento
  const handleMouseEnter = (e, date) => {
    if (window.innerWidth < 1024) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const tooltipWidth = 256 // w-64 = 16rem = 256px
    const tooltipHeight = 300 // altezza stimata
    
    let x = rect.left + rect.width / 2
    let y = rect.bottom + 8
    let showAbove = false
    let alignRight = false
    
    // Check se esce dal basso
    if (y + tooltipHeight > window.innerHeight) {
      y = rect.top - 8
      showAbove = true
    }
    
    // Check se esce da destra
    if (x + tooltipWidth / 2 > window.innerWidth - 20) {
      alignRight = true
    }
    
    // Check se esce da sinistra
    if (x - tooltipWidth / 2 < 20) {
      x = 20 + tooltipWidth / 2
    }
    
    setTooltipPosition({ x, y, showAbove, alignRight })
    setHoveredDate(date)
  }

  const handleMouseLeave = () => {
    setHoveredDate(null)
  }

  const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']

  return (
    <div ref={calendarRef} className={`card overflow-hidden ${className}`}>
      {/* CSS per pattern strisce */}
      <style>{`
        .bg-stripes {
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 4px,
            rgba(156, 163, 175, 0.15) 4px,
            rgba(156, 163, 175, 0.15) 8px
          );
        }
      `}</style>
      
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevMonth}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white min-w-[180px] text-center capitalize">
            {currentDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
          </h2>
          
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          {giorniIncompleti > 0 && (
            <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 px-2 py-1 rounded-full">
              {giorniIncompleti} incompleti
            </span>
          )}
          
          <button
            onClick={goToToday}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium px-3 py-1 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
          >
            Oggi
          </button>
        </div>
      </div>

      {/* Legenda chiusure */}
      {calendarioSettings && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-stripes border border-gray-300 rounded"></span>
            Chiuso
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-purple-100 border border-purple-300 rounded"></span>
            Festivo aperto
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-green-100 border border-green-300 rounded"></span>
            100%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></span>
            50-99%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-orange-100 border border-orange-300 rounded"></span>
            1-49%
          </span>
        </div>
      )}

      {/* Giorni settimana */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
        {weekDays.map(day => (
          <div
            key={day}
            className="py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Griglia giorni - con dimensioni controllate */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          const stats = getDayStats(day.date)
          const isToday = day.date.toDateString() === today.toDateString()
          const isSelected = selectedDate && day.date.toDateString() === new Date(selectedDate).toDateString()
          const isPast = day.date < today
          const bgColor = getDayBackgroundColor(stats, day.isCurrentMonth)
          const borderClass = getDayBorderClass(stats)
          const isFestivoLavorato = ['festivita_lavorata', 'patrono_lavorativo', 'apertura_straordinaria'].includes(stats.statoGiorno?.tipologia)
          
          return (
            <button
              key={index}
              onClick={() => handleDateClick(day.date)}
              onMouseEnter={(e) => handleMouseEnter(e, day.date)}
              onMouseLeave={handleMouseLeave}
              className={`
                relative p-1 border-b border-r border-gray-100 dark:border-gray-700
                transition-all duration-200
                min-h-[60px] sm:min-h-[70px] lg:min-h-[80px]
                ${day.isCurrentMonth ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50' : 'opacity-40'}
                ${bgColor}
                ${borderClass}
                ${isSelected ? 'ring-2 ring-primary-500 ring-inset' : ''}
                ${isToday ? 'font-bold' : ''}
              `}
              disabled={!day.isCurrentMonth}
            >
              {/* Numero giorno */}
              <div className={`
                text-sm
                ${isToday ? 'bg-primary-600 text-white w-6 h-6 rounded-full flex items-center justify-center mx-auto' : ''}
                ${!isToday && day.isCurrentMonth ? 'text-gray-900 dark:text-white' : 'text-gray-400'}
              `}>
                {day.date.getDate()}
              </div>
              
              {/* Emoji giorno speciale */}
              {day.isCurrentMonth && stats.statoGiorno && (
                <>
                  {/* Giorno chiuso */}
                  {!stats.statoGiorno.aperto && (
                    <div className="absolute top-0.5 right-0.5 text-xs">
                      {stats.statoGiorno.emoji || '🚫'}
                    </div>
                  )}
                  
                  {/* Festivo lavorato - mostra emoji speciale */}
                  {isFestivoLavorato && (
                    <div className="absolute top-0.5 right-0.5 text-xs flex items-center gap-0.5">
                      <span>{stats.statoGiorno.emojiOriginale || '🎉'}</span>
                      <span className="text-[10px]">⭐</span>
                    </div>
                  )}
                </>
              )}
              
              {/* Indicatori (solo se giorno aperto) */}
              {day.isCurrentMonth && stats.statoGiorno?.aperto !== false && (
                <>
                  {/* Mini progress bar */}
                  {stats.totaleMinori > 0 && stats.entries.length > 0 && (
                    <div className="absolute bottom-1 left-1 right-1 h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          stats.percentuale === 100 ? 'bg-green-500' :
                          stats.percentuale >= 50 ? 'bg-yellow-500' : 'bg-orange-500'
                        }`}
                        style={{ width: `${stats.percentuale}%` }}
                      />
                    </div>
                  )}
                  
                  {/* Badge urgenza */}
                  {stats.urgenze > 0 && (
                    <div className="absolute top-0 left-0 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </>
              )}
            </button>
          )
        })}
      </div>

      {/* Tooltip hover (desktop) */}
      {hoveredDate && (
        <DayTooltip
          date={hoveredDate}
          stats={getDayStats(hoveredDate)}
          position={tooltipPosition}
        />
      )}
    </div>
  )
}

// Tooltip per hover su giorno
function DayTooltip({ date, stats, position }) {
  const dateStr = date.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  })
  
  const tipologia = stats.statoGiorno?.tipologia
  const isFestivoLavorato = ['festivita_lavorata', 'patrono_lavorativo', 'apertura_straordinaria'].includes(tipologia)
  
  // Calcola stile posizionamento
  const tooltipStyle = {
    left: position.alignRight ? 'auto' : `${position.x}px`,
    right: position.alignRight ? '20px' : 'auto',
    top: position.showAbove ? 'auto' : `${position.y}px`,
    bottom: position.showAbove ? `${window.innerHeight - position.y}px` : 'auto',
    transform: position.alignRight ? 'none' : 'translateX(-50%)'
  }
  
  // Se giorno chiuso, mostra info chiusura
  if (stats.statoGiorno && !stats.statoGiorno.aperto) {
    return (
      <div
        className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 w-64"
        style={tooltipStyle}
      >
        <p className="font-medium text-gray-900 dark:text-white capitalize mb-2">{dateStr}</p>
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <span className="text-xl">{stats.statoGiorno.emoji || '🚫'}</span>
          <div>
            <p className="font-medium">Centro Chiuso</p>
            <p className="text-sm">{stats.statoGiorno.motivo}</p>
          </div>
        </div>
      </div>
    )
  }
  
  // Se festivo lavorato, mostra info speciale
  if (isFestivoLavorato) {
    return (
      <div
        className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-purple-200 dark:border-purple-700 p-3 w-64"
        style={tooltipStyle}
      >
        <p className="font-medium text-gray-900 dark:text-white capitalize mb-2">{dateStr}</p>
        <div className="flex items-center gap-2 mb-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <span className="text-xl">{stats.statoGiorno.emojiOriginale || '🎉'}</span>
          <div>
            <p className="font-medium text-purple-700 dark:text-purple-300">
              {stats.statoGiorno.motivoOriginale}
            </p>
            <p className="text-xs text-purple-600 dark:text-purple-400">⭐ Centro aperto</p>
          </div>
        </div>
        
        {/* Statistiche */}
        {stats.totaleMinori > 0 && (
          <>
            <div className="flex gap-3 mb-3 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-gray-600 dark:text-gray-400">{stats.presenze}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-gray-600 dark:text-gray-400">{stats.assenze}</span>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">Completamento</span>
                <span className={`font-medium ${
                  stats.percentuale === 100 ? 'text-green-600' :
                  stats.percentuale >= 50 ? 'text-yellow-600' : 'text-orange-600'
                }`}>
                  {stats.percentuale}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${
                    stats.percentuale === 100 ? 'bg-green-500' :
                    stats.percentuale >= 50 ? 'bg-yellow-500' : 'bg-orange-500'
                  }`}
                  style={{ width: `${stats.percentuale}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div
      className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 w-64 max-h-72 overflow-y-auto"
      style={tooltipStyle}
    >
      <p className="font-medium text-gray-900 dark:text-white capitalize mb-2">{dateStr}</p>
      
      {/* Statistiche */}
      <div className="flex gap-3 mb-3 text-sm">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-gray-600 dark:text-gray-400">{stats.presenze}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-orange-500" />
          <span className="text-gray-600 dark:text-gray-400">{stats.assenze}</span>
        </div>
        {stats.urgenze > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-600 dark:text-red-400">{stats.urgenze} urg.</span>
          </div>
        )}
      </div>
      
      {/* Progress */}
      {stats.totaleMinori > 0 && (
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500">Completamento</span>
            <span className={`font-medium ${
              stats.percentuale === 100 ? 'text-green-600' :
              stats.percentuale >= 50 ? 'text-yellow-600' : 'text-orange-600'
            }`}>
              {stats.percentuale}%
            </span>
          </div>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full ${
                stats.percentuale === 100 ? 'bg-green-500' :
                stats.percentuale >= 50 ? 'bg-yellow-500' : 'bg-orange-500'
              }`}
              style={{ width: `${stats.percentuale}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Lista minori mancanti */}
      {stats.minoriMancanti.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Da compilare ({stats.minoriMancanti.length}):
          </p>
          <div className="space-y-1">
            {stats.minoriMancanti.slice(0, 5).map(m => (
              <p key={m._id} className="text-sm text-gray-700 dark:text-gray-300 truncate">
                • {m.nomeCompleto}
              </p>
            ))}
            {stats.minoriMancanti.length > 5 && (
              <p className="text-xs text-gray-500">
                ...e altri {stats.minoriMancanti.length - 5}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * DayDetail - Dettaglio giorno selezionato
 */
export function DayDetail({ 
  date, 
  entries = [], 
  minori = [], 
  onClose,
  onNavigate,
  calendarioSettings = null,
  className = 'max-w-2xl mx-auto'
}) {
  const currentDate = new Date(date)
  currentDate.setHours(0, 0, 0, 0)
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const isToday = currentDate.getTime() === today.getTime()
  
  // Stato giorno per chiusure
  const statoGiorno = calendarioSettings ? getStatoGiorno(currentDate, calendarioSettings) : { aperto: true, tipologia: 'normale' }
  const isFestivoLavorato = ['festivita_lavorata', 'patrono_lavorativo', 'apertura_straordinaria'].includes(statoGiorno?.tipologia)
  
  const dateStr = currentDate.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
  
  // Formato per query param
  const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`
  
  // Calcola minori mancanti
  const minoriRegistratiIds = entries.map(e => 
    (typeof e.minore === 'object' ? e.minore._id : e.minore)?.toString()
  )
  const minoriMancanti = minori.filter(m => !minoriRegistratiIds.includes(m._id?.toString()))
  
  // Percentuale completamento
  const percentuale = minori.length > 0 
    ? Math.round((entries.length / minori.length) * 100) 
    : 0

  // Navigazione giorni
  const goToPrevDay = () => {
    const prevDate = new Date(currentDate)
    prevDate.setDate(prevDate.getDate() - 1)
    onNavigate?.(prevDate)
  }

  const goToNextDay = () => {
    const nextDate = new Date(currentDate)
    nextDate.setDate(nextDate.getDate() + 1)
    if (nextDate <= today) {
      onNavigate?.(nextDate)
    }
  }

  return (
    <div className={`card overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevDay}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Giorno precedente"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
            {dateStr}
            {isToday && (
              <span className="ml-2 text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 px-2 py-0.5 rounded-full">
                Oggi
              </span>
            )}
          </h3>
          
          <button
            onClick={goToNextDay}
            disabled={isToday}
            className={`p-2 rounded-lg transition-colors ${
              isToday ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={isToday ? 'Oggi' : 'Giorno successivo'}
          >
            <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Banner festivo lavorato */}
      {isFestivoLavorato && (
        <div className="px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-700">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{statoGiorno.emojiOriginale || '🎉'}</span>
            <div>
              <p className="font-medium text-purple-700 dark:text-purple-300">
                {statoGiorno.motivoOriginale}
              </p>
              <p className="text-sm text-purple-600 dark:text-purple-400">⭐ Centro aperto nonostante la festività</p>
            </div>
          </div>
        </div>
      )}

      {/* Banner giorno chiuso */}
      {statoGiorno && !statoGiorno.aperto && (
        <div className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{statoGiorno.emoji || '🚫'}</span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Centro Chiuso</p>
              <p className="text-sm text-gray-500">{statoGiorno.motivo}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar (solo se giorno aperto) */}
      {statoGiorno?.aperto !== false && minori.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📊 Completamento</span>
            <span className={`text-sm font-bold ${
              percentuale === 100 ? 'text-green-600' :
              percentuale >= 50 ? 'text-yellow-600' :
              percentuale > 0 ? 'text-orange-600' : 'text-gray-500'
            }`}>
              {entries.length}/{minori.length} ({percentuale}%)
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                percentuale === 100 ? 'bg-green-500' :
                percentuale >= 50 ? 'bg-yellow-500' :
                percentuale > 0 ? 'bg-orange-500' : 'bg-gray-300'
              }`}
              style={{ width: `${percentuale}%` }}
            />
          </div>
        </div>
      )}

      {/* Registrazioni inserite */}
      {entries.length > 0 && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20">
            <h4 className="text-sm font-medium text-green-800 dark:text-green-300 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Registrazioni inserite ({entries.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[300px] overflow-y-auto">
            {entries.map(entry => {
              const isPresent = entry.presente === true || entry.presente === 'Sì' || entry.presente === 'Presente'
              const isUrgent = entry.segnalazioneUrgente
              const minoreName = typeof entry.minore === 'object' ? entry.minore.nomeCompleto : 'Minore'
              
              return (
                <div
                  key={entry._id}
                  className={`flex items-center justify-between p-4 ${isUrgent ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {isUrgent ? (
                      <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    ) : isPresent ? (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <Clock className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className={`font-medium truncate ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-white'}`}>
                        {minoreName}
                        {isUrgent && <span className="ml-2 text-xs font-bold text-red-600">URGENTE</span>}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {entry.educatore}
                        {entry.orarioIngresso && ` • ${entry.orarioIngresso}`}
                        {entry.orarioUscita && ` - ${entry.orarioUscita}`}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/entries/${entry._id}/edit`}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors flex-shrink-0 ml-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span className="hidden sm:inline">Visualizza</span>
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Minori da compilare (solo se giorno aperto) */}
      {statoGiorno?.aperto !== false && minoriMancanti.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-orange-50 dark:bg-orange-900/20">
            <h4 className="text-sm font-medium text-orange-800 dark:text-orange-300 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Da compilare ({minoriMancanti.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[250px] overflow-y-auto">
            {minoriMancanti.map(minore => (
              <div key={minore._id} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Users className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{minore.nomeCompleto}</p>
                    <p className="text-sm text-gray-500 truncate">
                      Ref: {minore.educatoreReferente?.nome || minore.educatoreReferente?.username || 'Non assegnato'}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/entries/new/${minore._id}?data=${dateKey}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors flex-shrink-0 ml-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Compila</span>
                </Link>
              </div>
            ))}
          </div>
          
          {minoriMancanti.length > 3 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <Link
                to={`/entries/new?data=${dateKey}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                Compila tutti i mancanti
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {entries.length === 0 && minoriMancanti.length === 0 && (
        <div className="p-8 text-center">
          <CalendarIcon className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500">Nessun minore registrato nel centro</p>
        </div>
      )}

      {statoGiorno?.aperto !== false && entries.length === 0 && minori.length > 0 && minoriMancanti.length > 0 && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <Link
            to={`/entries/new?data=${dateKey}`}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors font-medium"
          >
            <Plus className="h-5 w-5" />
            Crea prima registrazione
          </Link>
        </div>
      )}
    </div>
  )
}

/**
 * ViewToggle - Toggle tra vista lista e calendario
 */
export function ViewToggle({ view, onChange }) {
  return (
    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
      <button
        onClick={() => onChange('list')}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          view === 'list'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <List className="h-4 w-4" />
        Lista
      </button>
      <button
        onClick={() => onChange('calendar')}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          view === 'calendar'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <CalendarIcon className="h-4 w-4" />
        Calendario
      </button>
    </div>
  )
}
